import { fundsInUse } from '../budget/settings';
import type { DomainEvent } from '../events';
import { insertMutation } from '../mutations';
import { defineRule, type Rule } from '../rules/engine';

/** Categories Payments falls back to when a payee has none (spec §6). */
export const LOAN_PAYMENT_KEY = 'loan_payment';
export const BILLS_KEY = 'bills_utilities';

/** A time strictly after `after`, so this write wins last-write-wins over the one it follows. */
function laterThan(now: string, after: string): string {
  const t = Date.parse(after);
  return Date.parse(now) > t ? now : new Date(t + 1).toISOString();
}

/**
 * Spec §6 Payments → Spending → Budget: a payment writes one ledger row on the id the producer
 * already put on the payment, with every field in the insert (category, fund, date):
 * - a bill, utility or loan payment is an expense: the payee's category (loans fall back to Loan
 *   payment, bills and utilities to Bills & Utilities), the lender or payee name as the note, and
 *   the payee's fund if one is set (never Budget's default fund: budget.deduct leaves Payments
 *   expenses alone). It's dated by the producer (a late
 *   payment on its due date). expense.created follows so budget.deduct joins the chain;
 * - a card statement payment is a transfer (decision 06): never spending, never deducted again.
 * Without Spending the rule doesn't run and the payment is recorded with no ledger row.
 */
export const paymentsPaidExpenseRule = defineRule<'payment.paid'>({
  id: 'payments.paid-expense',
  on: 'payment.paid',
  touches: ['payments', 'spending'],
  run(event, ctx) {
    if (!event.ledgerEntryId) return [];
    const at = laterThan(ctx.now, event.at);
    const fallbackKey = event.payeeKind === 'loan' ? LOAN_PAYMENT_KEY : BILLS_KEY;
    const categoryId = event.transfer
      ? null
      : (event.categoryId ?? ctx.read.getCategoryByKey(fallbackKey)?.id ?? null);
    // The payee's own fund or none, never Budget's default fund: payments are already held back
    // in what's left as commitments, and the default fund is a spending envelope.
    const fundId =
      event.transfer || !ctx.usable('budget') || !fundsInUse(ctx.read.getBudgetSettings())
        ? null
        : (event.fundId ?? null);
    // v0.5: the account it was paid from (a card statement payment too: it leaves that account).
    const accountId = ctx.usable('budget') ? (event.accountId ?? null) : null;
    const insert = insertMutation({
      table: 'ledger_entries',
      source: 'payments',
      rowId: event.ledgerEntryId,
      at,
      values: {
        date: event.date,
        amountMinor: event.amountMinor,
        currency: 'PHP',
        kind: event.transfer ? 'transfer' : 'expense',
        categoryId,
        method: null,
        fundId,
        accountId,
        sourceModule: 'payments',
        sourceId: event.paymentId,
        note: event.note,
        recordedTz: ctx.tz,
      },
    });
    if (event.transfer) return [insert];
    const created: DomainEvent = {
      type: 'expense.created',
      expenseId: event.ledgerEntryId,
      amountMinor: event.amountMinor,
      categoryId,
      fundId,
      date: event.date,
      method: null,
      sourceModule: 'payments',
      at,
      mutationId: insert.id,
    };
    return { mutations: [insert], events: [created] };
  },
});

export const PAYMENTS_RULES: Rule[] = [paymentsPaidExpenseRule];
